import { SystemStyleObject, Tabs as ChakraTabs } from '@chakra-ui/react'
import { createContext, useContext } from 'react'

const TabSizeContext = createContext<{ tabSize: ChakraTabs.RootProps['size'] }>(
  {
    tabSize: 'md',
  },
)

export interface TabsRootProps extends Omit<ChakraTabs.RootProps, 'variant'> {
  variant: 'line' | 'outline' | 'solid' | 'soft'
}
export const TabsRoot = ({ variant, size = 'md', ...rest }: TabsRootProps) => {
  let height: string | undefined = undefined
  switch (size) {
    case 'sm':
      height = '28px'
      break
    case 'md':
      height = '40px'
      break
    case 'lg':
      height = '52px'
      break
    default:
      break
  }

  return (
    <TabSizeContext.Provider value={{ tabSize: size }}>
      <ChakraTabs.Root
        variant={variant === 'soft' || variant === 'solid' ? 'plain' : variant}
        size={size}
        height={height}
        {...rest}
      />
    </TabSizeContext.Provider>
  )
}

export interface TabsListProps extends Omit<ChakraTabs.ListProps, 'variant'> {
  variant?: 'line' | 'outline' | 'solid' | 'soft'
}
export const TabsList = ({ variant, ...rest }: TabsListProps) => {
  const { tabSize } = useContext(TabSizeContext)
  let height: string | undefined = undefined
  switch (tabSize) {
    case 'sm':
      height = '28px'
      break
    case 'md':
      height = '40px'
      break
    case 'lg':
      height = '52px'
      break
    default:
      break
  }

  return (
    <ChakraTabs.List
      h={height}
      minH={height}
      borderBottomWidth={variant === 'line' ? '2px' : undefined}
      {...rest}
    />
  )
}

export interface TabsTriggerProps extends ChakraTabs.TriggerProps {
  variant: 'line' | 'outline' | 'solid' | 'soft'
}

export const TabsTrigger = ({ variant, ...rest }: TabsTriggerProps) => {
  const { tabSize } = useContext(TabSizeContext)
  let height: string | undefined = undefined
  let fontSize: string | undefined = undefined
  switch (tabSize) {
    case 'sm':
      height = '28px'
      fontSize = 'sm'
      break
    case 'md':
      height = '40px'
      fontSize = 'md'
      break
    case 'lg':
      height = '52px'
      fontSize = 'lg'
      break
    default:
      break
  }

  const _selected: SystemStyleObject = {}
  const _hover: SystemStyleObject = {}
  switch (variant) {
    case 'line':
      _selected.color = 'colorPalette.500'
      _selected['&:before'] = {
        bg: 'colorPalette.500',
        '--indicator-offset-y': '0px',
      }
      _hover.bgColor = 'colorPalette.500/8'
      break
    case 'outline':
      _selected.bgColor = 'white'
      _selected.borderRadius = '6px 6px 0px 0px'
      _selected['&:hover'] = {
        bgColor:
          'linear-gradient(0deg, var(--hover-bg-color) 0%, var(--hover-bg-color) 100%), var(--chakra-colors-white)',
      }
      _hover['--hover-bg-color'] = '{colors.colorPalette.500/8}'
      _hover.bgColor = 'var(--hover-bg-color)'
      break
    case 'solid':
      _selected.color = 'white'
      _selected.bgColor = 'colorPalette.500'
      _selected.borderRadius = 'full'
      _hover['&:not([aria-selected=true], [data-selected])'] = {
        bgColor: 'colorPalette.100',
        borderRadius: 'full',
      }
      break
    case 'soft':
      _selected.color = 'colorPalette.500'
      _selected.bgColor = 'colorPalette.100'
      _selected.borderRadius = 'full'
      _hover.bgColor = 'colorPalette.100'
      _hover.borderRadius = 'full'
      break
  }

  return (
    <ChakraTabs.Trigger
      h={height}
      minH={height}
      fontSize={fontSize}
      _selected={_selected}
      _hover={_hover}
      {...rest}
    />
  )
}

export const TabsIndicator = ChakraTabs.Indicator

export const TabsContent = ChakraTabs.Content

export const TabsContentGroup = ChakraTabs.ContentGroup
